import {
  useCallback,
  useState,
} from 'react';
import { usePrevious } from 'react-use';
import { TabRenderer } from './TabRenderer';
import { TabsProvider } from './TabsProvider';
import { getActiveTabIndex } from './getActiveTabIndex';
import { useControlledWarning } from './useControlledWarning';
import type {
  TabProps,
  TabsProps,
} from './Tabs.types';

export function Tabs({
  activeTabId,
  defaultActiveTabId,
  onTabChange = () => {},
  tabLabelComponent,
  renderTabLabel,
  renderTabBar,
  renderTabContent,
  tabBarProps,
  tabContainerProps,
  tabLabelProps,
  tabContentAnimation,
  children,
  ...containerProps
}: TabsProps) {
  const [internalActiveTabId, setInternalActiveTabId] = useState<string | null>(
    activeTabId ? null : (defaultActiveTabId ?? null),
  );
  const [tabs, setTabs] = useState<TabProps[]>([]);

  useControlledWarning({
    activeTabId,
    defaultActiveTabId,
  });

  const registerTab = useCallback((tab: TabProps) => {
    if (tabs.find((tabData) => (tabData.id === tab.id))) {
      return;
    }
    setTabs((prevTabs) => ([...prevTabs, tab]));
  }, [tabs]);

  const unregisterTab = useCallback((tabId: string) => {
    if (!tabs.find((tabData) => (tabData.id === tabId))) {
      return;
    }
    setTabs((prevTabs) => prevTabs.filter((tabData) => (tabData.id !== tabId)));
  }, [tabs]);

  const activeTab = activeTabId ?? internalActiveTabId ?? null;

  const setActiveTab = useCallback((tabId: string) => {
    if (
      internalActiveTabId
      && internalActiveTabId !== tabId
    ) {
      setInternalActiveTabId(tabId);
    }
    if (activeTab !== tabId) {
      onTabChange(tabId);
    }
  }, [
    activeTab,
    internalActiveTabId,
    onTabChange,
  ]);

  const prevActiveTabId = usePrevious(activeTab) ?? null;
  const activeTabIndex = getActiveTabIndex(tabs, activeTab);
  const prevActiveTabIndex = getActiveTabIndex(tabs, prevActiveTabId);

  return (
    <TabsProvider
      tabs={tabs}
      activeTabId={activeTab}
      activeTabIndex={activeTabIndex}
      prevActiveTabId={prevActiveTabId}
      prevActiveTabIndex={prevActiveTabIndex}
      registerTab={registerTab}
      unregisterTab={unregisterTab}
      setActiveTab={setActiveTab}
      tabContentAnimation={tabContentAnimation}
    >
      <TabRenderer
        tabLabelComponent={tabLabelComponent}
        renderTabLabel={renderTabLabel}
        renderTabBar={renderTabBar}
        renderTabContent={renderTabContent}
        tabBarProps={tabBarProps}
        tabContainerProps={tabContainerProps}
        tabLabelProps={tabLabelProps}
        {...containerProps}
      />
      {children}
    </TabsProvider>
  );
}
